/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, useNavigate } from 'react-router-dom';

import styles from './Home.module.scss';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  selectCreatorMode,
  selectDocumentVersionById,
  selectIsSearching,
  selectMessages,
  selectPublishedOnly,
} from '../chat/chatSlice';
import AudienceContainer from '../chat/components/Audience/AudienceContainer';
import { SEARCH_PARAM, useNavigateToTopic, useAudienceFromSearchParamsOrLocalStorage } from '../chat/useHooks';
import { fetchAllDynamicSuggestionsThunk, selectDynamicSuggestionsForUser, SuggestionType } from '../chat/searchSlice';
import StyledPopover from '../chat/components/InnerMessage/StyledPopover';
import AutocompleteRenderer from '../Autocomplete/AutocompleteRenderer';
import { selectPearlUserInfo } from '../Login/userSlice';
import { SearchInput } from '../chat/components/SearchInput';
import { PersonalRecommendationTypes, specialtyIcons, SuggestionTypes } from './homeUtils';
import { analyticsTrack } from '../analytics_utils';
import { useIsSidebarOpen } from '../sidemenu/SideMenu';
import VideoComponent from './VideoPlayer';
import GitCommit from '../../../_git_commit';
import { InfoIcon } from './InfoIcon';
import Tooltip from '@mui/material/Tooltip';
import GitInfo from '../Git/GitInfo';
import { useIsConsumerWebUser } from '../permissions';
import MedPearlLogo from './Medpearl_logo_hero.svg';
import MuscQuickStartPopover from '../musc/MuscQuickStartPopOver';

export type HomePageProps = {
  creatorMode?: boolean;
  searchCategories?: boolean;
  isMuscUser?: boolean;
};
export default function HomePage({ creatorMode, isMuscUser }: HomePageProps) {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const handleDialog = () => {
    setDialogOpen(!dialogOpen);
  };

  const [muscGuideDialogOpen, setMuscDialogOpen] = useState<boolean>(false);
  const handleMuscDialog = () => {
    setMuscDialogOpen(!muscGuideDialogOpen);
  };

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isCreatorMode = useAppSelector(selectCreatorMode);
  const publishedOnly = useAppSelector(selectPublishedOnly);
  const isSearching = useAppSelector(selectIsSearching);

  const disableAudience = true;
  const [audienceFilter, setAudienceFilter] = useAudienceFromSearchParamsOrLocalStorage(true, disableAudience);
  const compact = useMediaQuery('(max-width: 900px)');

  const messages = useAppSelector(selectMessages);

  const documentVersionId = messages.find((m) => m.pages?.length)?.pages?.[0]?.document_version_id;
  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, documentVersionId));

  const pearlUserInfo = useAppSelector(selectPearlUserInfo);

  const { specialities, recommendedTopics, viewedTopics } = useAppSelector(selectDynamicSuggestionsForUser);
  const [showAllSpecialities, setShowAllSpecialities] = useState<boolean>(false);
  const [value, setValue] = useState<SuggestionTypes>(PersonalRecommendationTypes[0].label);
  const [listToShow, setListToShow] = useState<SuggestionType[]>(recommendedTopics || []);
  const open = useIsSidebarOpen();
  const isConsumerWebUser = useIsConsumerWebUser();
  // I want to manually take care of the transitions because I use useMemo to memoize suggestions.
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1000); // Delay to ensure smooth transition
    return () => clearTimeout(timer);
  }, []);
  const logClick = useCallback(
    (event: any) => {
      if (event.shiftKey) {
        // Go to /admin/logs
        navigate('/admin/logs');
      }
    },
    [navigate]
  );
  useEffect(() => {
    if (value === 'Recommended topics') setListToShow(recommendedTopics || []);
    // else if (value === 'Trending topics') setListToShow(trendingTopics || []);
    else if (value === 'Viewed topics') setListToShow(viewedTopics || []);
  }, [value, recommendedTopics, viewedTopics]);
  useEffect(() => {
    void dispatch(fetchAllDynamicSuggestionsThunk());
  }, [dispatch]);
  const handleChipClick = (label: string, mixpanelLabel: string) => {
    setValue(label as SuggestionTypes);
    console.log('Clicked tab:', mixpanelLabel);
    handleWelcomePageWidgetClick('topics_tab', mixpanelLabel, mixpanelLabel);
  };

  const navigateToTopic = useNavigateToTopic(publishedOnly, '/chat');
  function sendText(text: string, slug?: string) {
    if (text) navigateToTopic(text, slug, 'userTyped');
    return Promise.resolve();
  }

  const handleSpecialtySearch = (specialty: string) => {
    void sendText(specialty);
    analyticsTrack(
      'welcomepage_widget_click',
      { category: 'specialty', name: specialty, source_section: 'specialties' },
      pearlUserInfo
    );
  };

  function handleWelcomePageWidgetClick(category: string, label: string, source?: string) {
    analyticsTrack(
      'welcomepage_widget_click',
      {
        category: category,
        name: label,
        source_section: source,
      },
      pearlUserInfo
    );
  }

  function buildHomeTopicUrl(topic: SuggestionType) {
    if (!topic.slug) {
      return `#${encodeURI(topic.title)}`;
    }

    const params = new URLSearchParams();
    params.set(SEARCH_PARAM.SLUG, topic.slug);
    params.set(SEARCH_PARAM.TITLE, topic.title);
    return `/chat?${params.toString()}`;
  }

  // Memoize the specialities to avoid unnecessary API calls.
  const topSpecialties = useMemo(() => {
    return Array.isArray(specialities) ? specialities.slice(0, 5) : [];
  }, [specialities]);
  const sortedSpecialties = useMemo(() => {
    return Array.isArray(specialities)
      ? specialities
          .filter((item) => typeof item.specialty === 'string')
          .sort((a, b) => a.specialty.localeCompare(b.specialty))
      : [];
  }, [specialities]);

  function handleSpecialtyShowAllClick() {
    setShowAllSpecialities(true);
    handleWelcomePageWidgetClick('specialty', 'show_all', 'specialties');
  }

  return (
    <AudienceContainer
      audienceFilter={audienceFilter}
      publishedOnly={publishedOnly}
      chatRef={useRef(null)} //passing null ref to audience container as we don't have chat in home page
      documentVersion={documentVersion}
      onChangeAudience={setAudienceFilter}
      showPublishedOnly={creatorMode}
      showFeedback={!creatorMode}
      compact={compact}
      hideAudience
    >
      <div className={`${styles.homeParentContainer} ${isCreatorMode ? styles.creatorHome : ''}`}>
        <Box className={`${styles.container} ${isCreatorMode ? styles.creatorContainer : ''}`}>
          <div className={`${styles.heroContainer} ${isCreatorMode ? styles.creatorHeroContainer : ''}`}>
            {isCreatorMode ? (
              <div className={styles.creatorIdentity}>
                <Typography className={styles.creatorLogo}>
                  <img src={MedPearlLogo} alt="MedPearl" />
                </Typography>
                <div className={styles.creatorModeLabel}>
                  <span />
                  <Typography>Creator Mode</Typography>
                  <span />
                </div>
                <Typography className={styles.creatorTagline}>
                  Build, refine, and share trusted medical knowledge.
                </Typography>
              </div>
            ) : (
              <>
                {/* Show logo only for teams. Planning to move this to shared repo to reduce duplication */}
                {!isConsumerWebUser && (
                  <Typography className={styles.superHeading}>
                    {' '}
                    <img src={MedPearlLogo} alt="MedPearl" />
                  </Typography>
                )}
                <Typography className={styles.heroHeading}>
                  Actionable Medical Knowledge at the Point of Care
                </Typography>
                <Typography className={styles.heroSubHeading}>Making Every Visit Count</Typography>
              </>
            )}
            {/* use algolia autocomplete for consumer */}
            {!isCreatorMode ? (
              <AutocompleteRenderer
                onEnter={sendText}
                audience={audienceFilter}
                publishedOnly={publishedOnly}
                className="home-input-autocomplete"
              />
            ) : (
              <SearchInput
                isSearching={isSearching}
                audience={audienceFilter}
                publishedOnly={publishedOnly}
                onEnter={sendText}
              />
            )}
          </div>
          <div className={`${styles.wrapperDiv} ${isCreatorMode ? styles.creatorWrapper : ''}`}>
            {!showAllSpecialities ? (
              <div className={styles.dynamicSuggestions}>
                <div className={styles.customSearch}>
                  <div className={styles.recommendationTabs}>
                    <div className={styles.recommendationHeaderPanel}>
                      <Stack direction="row" spacing={2} mb={2} style={{ marginBottom: 0 }}>
                        {PersonalRecommendationTypes.map((item, index) => (
                          <Chip
                            key={index}
                            label={item.label}
                            clickable
                            variant="filled"
                            onClick={() => handleChipClick(item.label, item.mixpanelLabel)}
                            data-testid="recommendation-labels"
                            sx={{
                              '&.MuiChip-root': {
                                backgroundColor: value === item.label ? 'var(--npi-input)' : 'var(--home-background)',
                                color: 'var(--messageresponse)',
                                fontWeight: 400,
                                border: '1px solid var(--npi-input)',
                                '&:hover': {
                                  background: '#E1EAF8',
                                },
                                '&:active': {
                                  background: '#D2E0F3',
                                },
                              },
                            }}
                          />
                        ))}
                      </Stack>
                      <Box className={styles.personalRecommendationsBox}>
                        {
                          <ul
                            className={`${styles.listContainer} ${styles.transition} ${
                              isVisible ? styles.visible : ''
                            }`}
                          >
                            {listToShow.map((topic, index) => (
                              <li key={index} className={styles.topic}>
                                <Link
                                  className={styles.topicLink}
                                  to={buildHomeTopicUrl(topic)}
                                  onClick={() =>
                                    handleWelcomePageWidgetClick(
                                      'topic',
                                      topic.title,
                                      PersonalRecommendationTypes.find((x) => x.label === value)?.mixpanelLabel
                                    )
                                  }
                                  data-testid="personal-recommendation-options"
                                >
                                  <span>{topic.title}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        }
                      </Box>{' '}
                    </div>
                  </div>
                  <div className={open ? styles.specialtySearchOpen : styles.specialtySearchClosed}>
                    <div className={styles.heading}>
                      <Chip
                        variant="filled"
                        label="Topics by specialty"
                        style={{
                          backgroundColor: 'var(--npi-input)',
                          color: 'var(--messageresponse',
                          border: '1px solid var(--npi-input)',
                        }}
                      />
                      <span
                        data-underline="true"
                        data-testid="show-all-specialties-btn"
                        onClick={() => handleSpecialtyShowAllClick()}
                      >
                        Show all
                      </span>
                    </div>
                    <div className={`${styles.specialtyList} ${styles.transition}`}>
                      <ul className={`${styles.listContainer} ${styles.transition} ${isVisible ? styles.visible : ''}`}>
                        {topSpecialties?.map(({ specialty }: { specialty: string }, index: number) => (
                          <li key={specialty} className={styles.noStyletopic}>
                            {' '}
                            <Chip
                              data-testid="search-chips"
                              label={
                                <div className={styles.chip}>
                                  {specialtyIcons[specialty] && (
                                    <span className={styles.specialtyIcon}>{specialtyIcons[specialty]}</span>
                                  )}
                                  {specialty}
                                </div>
                              }
                              key={index}
                              variant="transparent"
                              onClick={() => handleSpecialtySearch(specialty)}
                              className={styles.specialtyChip}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                {isMuscUser && (
                  <div className={styles.helperVideos}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <VideoComponent
                        url="https://youtu.be/h2bzzY695_I"
                        title="Interview with Vanessa Diaz, MD and Megan Fulton, PA"
                        pearlUserInfo={pearlUserInfo}
                      />
                      <VideoComponent
                        url="https://youtu.be/QmM2e_lmyLE"
                        title="3 1/2 minute video on how to use MedPearl"
                        pearlUserInfo={pearlUserInfo}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '20px' }} data-id="epic-video">
                      <VideoComponent
                        url="https://youtu.be/RW53GmB5ggA"
                        title="30 second video on how to access MedPearl in EPIC (no sound)"
                        pearlUserInfo={pearlUserInfo}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.allSpecilities}>
                <span
                  className={styles.backtoSearch}
                  data-testid="hide-specialties-btn"
                  onClick={() => setShowAllSpecialities(false)}
                >
                  <KeyboardArrowLeftIcon />
                  Back
                </span>
                <div className={styles.allSpecilitiesList}>
                  {sortedSpecialties?.map(({ specialty }: { specialty: string }, index: number) => (
                    <Chip
                      label={
                        <div className={styles.chip}>
                          {specialtyIcons[specialty] && (
                            <span className={styles.specialtyIcon}>{specialtyIcons[specialty]}</span>
                          )}
                          {specialty}
                        </div>
                      }
                      key={index}
                      variant="transparent"
                      onClick={() => handleSpecialtySearch(specialty)}
                      className={styles.specialtyChip}
                      data-showall="true"
                      data-testid="specialty-chip"
                    />
                  ))}{' '}
                </div>
              </div>
            )}
            <div
              className={styles.footerNew}
              data-testid="home-footer"
              data-ismusc-user={isMuscUser ? 'true' : 'false'}
            >
              {' '}
              <strong>MedPearl is written by clinicians for the clinicians,</strong> bringing codified medical knowledge
              to the point of care, enhancing treatment plans and specialty referrals to ease patient access and
              clinician cognitive burden across the care continuum. It is not meant for non-clinician use nor is it
              meant to replace the clinical judgement of a clinician. Read more&nbsp;
              <span onClick={handleDialog} data-hyperlink="true">
                here
              </span>
              .{dialogOpen && <StyledPopover handleDialog={handleDialog} />}
              {isMuscUser ? (
                <>
                  <div className={styles.bottomContainer}>
                    <div className={styles.muscQuickStart} data-testid="musc-guide-btn" onClick={handleMuscDialog}>
                      <InfoIcon /> Quickstart Guide
                    </div>
                    <div onClick={logClick} style={{ cursor: 'pointer' }}>
                      Version: {GitCommit.logMessage} {GitCommit.datetime}
                    </div>
                  </div>
                  {muscGuideDialogOpen && <MuscQuickStartPopover handleDialog={handleMuscDialog} />}
                </>
              ) : (
                <div className={styles.metaData}>
                  <div
                    className={`${styles.dateRangeContainer}`}
                    data-teams-user={!isConsumerWebUser ? 'true' : 'false'}
                  >
                    <Tooltip
                      title="This summary is based on the data available for contextualization to MedPearl. Please see the EHR to review any incomplete or more recent results"
                      className={styles.disclaimer}
                      placement="top"
                    >
                      <span className={styles.disclaimerText}>Data Disclaimer</span>
                    </Tooltip>
                  </div>
                  <div className={styles.versionAndMetadata} onClick={logClick}>
                    <GitInfo />
                  </div>
                </div>
              )}
            </div>
          </div>
        </Box>
      </div>
    </AudienceContainer>
  );
}
